import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { usersAPI, uploadAPI } from '../../services/apiServices';
import { Alert, Modal } from '../../components/common/UI';
import { UserIcon } from '../../components/common/Icons';
import { formatPlanPrice, REGIONS } from '../../utils/plans';
import { APP_VERSION } from '../../config/appVersion';
import { formatPhoneInput } from '../../utils/inputFormat';
import { requiredFieldMessage, requiredFieldsMessage } from '../../utils/validationMessages';
import { formatLocalizedDate } from '../../utils/dateUtils';
import AuthenticatedImage from '../../components/common/AuthenticatedImage';

const SHARE_DEFAULTS = {
  name: true,
  email: true,
  phone: false,
  website: false,
  address: false,
};

const PROFILE_DEFAULTS = {
  name: '',
  email: '',
  phone: '',
  whatsapp: '',
  website: '',
  street: '',
  number: '',
  complement: '',
  neighborhood: '',
  city: '',
  state: '',
  zipCode: '',
  avatar: '',
  profileShareSettings: SHARE_DEFAULTS,
};

const buildProfile = (user) => ({
  ...PROFILE_DEFAULTS,
  ...user,
  profileShareSettings: {
    ...SHARE_DEFAULTS,
    ...(user?.profileShareSettings || {}),
  },
});

const ProfilePage = () => {
  const { t, i18n } = useTranslation();
  const { user, updateUser, logout } = useAuth();
  const fileInputRef = useRef(null);
  const [profile, setProfile] = useState(buildProfile(user));
  const [password, setPassword] = useState({ current: '', next: '', confirm: '' });
  const [profileErrors, setProfileErrors] = useState({});
  const [passwordErrors, setPasswordErrors] = useState({});
  const [profileMsg, setProfileMsg] = useState(null);
  const [passwordMsg, setPasswordMsg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  const [deleteStep, setDeleteStep] = useState(0);
  const [deleteReason, setDeleteReason] = useState('');
  const [deleteError, setDeleteError] = useState('');
  const [deletingAccount, setDeletingAccount] = useState(false);
  const [addressOpen, setAddressOpen] = useState(false);
  const [securityOpen, setSecurityOpen] = useState(false);

  useEffect(() => {
    setProfile(buildProfile(user));
  }, [user]);

  const handleAvatarChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setProfileMsg({ type: 'error', text: t('clientForm.invalidImage') });
      return;
    }

    setUploadingAvatar(true);
    setProfileMsg(null);
    try {
      const { data } = await uploadAPI.image(file);
      setProfile((current) => ({ ...current, avatar: data.data.url }));
    } catch (error) {
      setProfileMsg({ type: 'error', text: error.response?.data?.error || t('profile.uploadError') });
    } finally {
      setUploadingAvatar(false);
      event.target.value = '';
    }
  };

  const saveProfile = async (event) => {
    event.preventDefault();
    const errors = {};
    if (!String(profile.name || '').trim()) errors.name = requiredFieldMessage(t, t('profile.fullName'));
    const contact = String(profile.phone || profile.whatsapp || '').trim();
    if (contact && contact.length < 6) errors.phone = t('profile.phoneRequired', 'Telefone invalido.');
    setProfileErrors(errors);
    if (Object.keys(errors).length) {
      setProfileMsg({ type: 'error', text: Object.values(errors)[0] });
      return;
    }
    setSaving(true);
    setProfileMsg(null);
    try {
      const { data } = await usersAPI.updateProfile({
        name: profile.name,
        phone: contact || null,
        whatsapp: contact || null,
        website: profile.website,
        street: profile.street,
        number: profile.number,
        complement: profile.complement,
        neighborhood: profile.neighborhood,
        city: profile.city,
        state: profile.state,
        zipCode: profile.zipCode,
        profileShareSettings: profile.profileShareSettings,
        avatar: profile.avatar || null,
      });
      updateUser(data.data);
      setProfileMsg({ type: 'success', text: t('profile.updateSuccess') });
    } catch (error) {
      setProfileMsg({ type: 'error', text: error.response?.data?.error || t('profile.updateError') });
    } finally {
      setSaving(false);
    }
  };

  const changePassword = async (event) => {
    event.preventDefault();
    setPasswordMsg(null);
    const errors = {};
    const missingLabels = [];
    if (!password.current) {
      errors.current = requiredFieldMessage(t, t('profile.currentPassword'));
      missingLabels.push(t('profile.currentPassword'));
    }
    if (!password.next) {
      errors.next = requiredFieldMessage(t, t('profile.newPassword'));
      missingLabels.push(t('profile.newPassword'));
    }
    if (!password.confirm) {
      errors.confirm = requiredFieldMessage(t, t('profile.confirmNewPassword'));
      missingLabels.push(t('profile.confirmNewPassword'));
    }
    if (missingLabels.length) {
      setPasswordErrors(errors);
      setPasswordMsg({ type: 'error', text: requiredFieldsMessage(t, missingLabels) });
      return;
    }
    if (password.next !== password.confirm) {
      setPasswordErrors({ confirm: t('profile.passwordMismatch') });
      setPasswordMsg({ type: 'error', text: t('profile.passwordMismatch') });
      return;
    }
    if (password.next.length < 8) {
      setPasswordErrors({ next: t('profile.passwordTooShort') });
      setPasswordMsg({ type: 'error', text: t('profile.passwordTooShort') });
      return;
    }
    setPasswordErrors({});

    setChangingPassword(true);
    try {
      await usersAPI.changePassword(password.current, password.next);
      setPassword({ current: '', next: '', confirm: '' });
      setPasswordMsg({ type: 'success', text: t('profile.passwordSuccess') });
    } catch (error) {
      setPasswordMsg({ type: 'error', text: error.response?.data?.error || t('profile.passwordError') });
    } finally {
      setChangingPassword(false);
    }
  };

  const updateField = (field, value) => {
    setProfile((current) => ({ ...current, [field]: value }));
    setProfileErrors((current) => ({ ...current, [field]: '' }));
  };
  const toggleShare = (field) => setProfile((current) => ({
    ...current,
    profileShareSettings: {
      ...current.profileShareSettings,
      [field]: !current.profileShareSettings?.[field],
    },
  }));

  const confirmDeleteAccount = async () => {
    setDeletingAccount(true);
    try {
      await usersAPI.deleteProfile(deleteReason);
      await logout();
    } catch (error) {
      setProfileMsg({ type: 'error', text: error.response?.data?.error || t('profile.deleteError') });
      setDeleteStep(0);
    } finally {
      setDeletingAccount(false);
    }
  };

  const subscription = user?.subscription;
  const region = subscription?.region || 'BR';
  const plan = subscription?.plan || 'basic';
  const planLabel = plan === 'standard' ? t('plans.standard.title') : t('plans.basic.title');
  const expiration = subscription?.endDate || subscription?.trialEndsAt;

  return (
    <div className="mx-auto max-w-[1040px]">
      <div className="mb-4">
        <h1 className="text-3xl font-bold tracking-tight text-slate-900">{t('profile.title')}</h1>
        <p className="mt-1 text-base text-slate-600">{t('profile.subtitle')}</p>
      </div>
      {!profile.name && (
        <Alert variant="info" style={{ marginBottom: '1rem' }}>
          {t('profile.completeProfileNotice')}
        </Alert>
      )}

      <div className="grid gap-4 lg:grid-cols-[260px_1fr]">
        <aside className="rounded-lg border border-slate-200 bg-white p-4 shadow-soft">
          <div className="flex flex-col items-center text-center">
            <div className="relative">
              <button
                className="flex h-20 w-20 items-center justify-center overflow-hidden rounded-full bg-sky-50 text-sky-500 ring-1 ring-sky-100 transition hover:ring-sky-300"
                type="button"
                onClick={() => fileInputRef.current?.click()}
                aria-label={t('profile.changePhoto')}
                disabled={uploadingAvatar}
              >
                {profile.avatar ? (
                  <AuthenticatedImage source={profile.avatar} alt="" className="h-full w-full object-cover" />
                ) : (
                  <UserIcon className="h-9 w-9" />
                )}
              </button>
              <button
                className="absolute bottom-0 right-0 flex h-8 w-8 items-center justify-center rounded-full bg-sky-500 text-xs font-black text-white shadow-soft"
                type="button"
                onClick={() => fileInputRef.current?.click()}
                aria-label={t('profile.changePhoto')}
                disabled={uploadingAvatar}
              >
                {uploadingAvatar ? '...' : '+'}
              </button>
              <input ref={fileInputRef} type="file" accept="image/*" className="sr-only" onChange={handleAvatarChange} />
            </div>
            <h2 className="mt-3 font-bold text-slate-900">{profile.name || t('profile.userFallback')}</h2>
            <p className="mt-1 text-sm text-slate-600">{profile.email}</p>
            <span className="mt-3 rounded-full bg-emerald-50 px-3 py-1 text-xs font-bold text-emerald-700">
              {planLabel}
            </span>
          </div>
          <div className="mt-4 rounded-lg bg-slate-50 p-3 text-sm text-slate-700">
            <p className="font-bold text-slate-900">{t('profile.mySubscription')}</p>
            <p className="mt-2">{planLabel} - {REGIONS[region]?.name || region}</p>
            <p>{formatPlanPrice(region, plan, i18n.language)}{t('plans.month')}</p>
            <p>{subscription?.isTrialActive ? t('profile.trialDaysLeft', { count: subscription.daysLeftInTrial || 0 }) : t(`plans.paymentLabels.${subscription?.paymentStatus || 'paid'}`)}</p>
            <p>{t('profile.expiresAt')}: {formatLocalizedDate(expiration, i18n.language) || t('common.notProvided')}</p>
            <Link
              to="/subscription"
              className="mt-3 inline-flex h-9 w-full items-center justify-center rounded-lg bg-sky-500 px-4 text-sm font-bold text-white"
            >
              {subscription?.isTrialActive ? t('profile.subscribeNow') : t('profile.manageSubscription')}
            </Link>
          </div>
        </aside>

        <div className="space-y-4">
          <section className="rounded-lg border border-slate-200 bg-white p-4 shadow-soft">
            <h2 className="text-lg font-bold text-slate-900">{t('profile.personalInfo')}</h2>
            {profileMsg && <Alert variant={profileMsg.type} onClose={() => setProfileMsg(null)} style={{ marginTop: '1rem' }}>{profileMsg.text}</Alert>}
            <form onSubmit={saveProfile} className="mt-5 space-y-4">
              <div className="grid gap-4 md:grid-cols-2">
                <Field label={t('profile.fullName')} value={profile.name} onChange={(value) => updateField('name', value)} placeholder={t('profile.fullNamePlaceholder')} error={profileErrors.name} />
                <Field label={t('auth.email')} value={profile.email} disabled placeholder={t('auth.emailPlaceholder')} />
              </div>
              <Field label={t('profile.phone')} value={profile.phone || profile.whatsapp || ''} onChange={(value) => {
                const formatted = formatPhoneInput(value, i18n.language);
                updateField('phone', formatted);
                updateField('whatsapp', formatted);
              }} placeholder={t('auth.phonePlaceholder')} error={profileErrors.phone} />
              <Field label={t('contacts.website')} value={profile.website || ''} onChange={(value) => updateField('website', value)} placeholder="empresa.com" />
              <div className="rounded-lg border border-slate-200 bg-white">
                <button
                  type="button"
                  className="flex w-full items-center justify-between px-3 py-2 text-sm font-bold text-slate-800"
                  onClick={() => setAddressOpen((value) => !value)}
                >
                  {t('contacts.address')}
                  <span className="text-xs text-slate-500">{addressOpen ? t('common.cancel') : t('common.edit')}</span>
                </button>
                {addressOpen && (
                  <div className="space-y-4 border-t border-slate-200 p-3">
                    <div className="grid gap-4 md:grid-cols-[1fr_120px]">
                      <Field label={t('clientForm.street')} value={profile.street || ''} onChange={(value) => updateField('street', value)} />
                      <Field label={t('clientForm.number')} value={profile.number || ''} onChange={(value) => updateField('number', value)} />
                    </div>
                    <div className="grid gap-4 md:grid-cols-3">
                      <Field label={t('clientForm.city')} value={profile.city || ''} onChange={(value) => updateField('city', value)} />
                      <Field label={t('clientForm.state')} value={profile.state || ''} onChange={(value) => updateField('state', value)} />
                      <Field label={t('clientForm.zipCode')} value={profile.zipCode || ''} onChange={(value) => updateField('zipCode', value)} />
                    </div>
                    <div className="grid gap-4 md:grid-cols-2">
                      <Field label={t('clientForm.neighborhood')} value={profile.neighborhood || ''} onChange={(value) => updateField('neighborhood', value)} />
                      <Field label={t('clientForm.complement')} value={profile.complement || ''} onChange={(value) => updateField('complement', value)} />
                    </div>
                  </div>
                )}
              </div>
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                <p className="mb-3 text-sm font-bold text-slate-900">{t('profile.shareSettings')}</p>
                <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                  {['name', 'email', 'phone', 'website', 'address'].map((field) => (
                    <label key={field} className="flex items-center gap-2 text-sm font-semibold text-slate-700">
                      <input
                        type="checkbox"
                        checked={Boolean(profile.profileShareSettings?.[field])}
                        onChange={() => toggleShare(field)}
                      />
                      {t(`profile.share.${field}`)}
                    </label>
                  ))}
                </div>
              </div>
              <div className="border-t border-slate-200 pt-4">
                <button
                  className="inline-flex h-10 items-center justify-center rounded-lg bg-sky-500 px-5 text-sm font-bold text-white shadow-soft transition hover:bg-sky-600"
                  type="submit"
                  disabled={saving}
                >
                  {saving ? t('common.saving') : t('profile.saveChanges')}
                </button>
              </div>
            </form>
          </section>

          <section className="rounded-lg border border-slate-200 bg-white p-3 shadow-soft">
            <button type="button" className="flex w-full items-center justify-between text-left" onClick={() => setSecurityOpen((value) => !value)}>
              <span className="text-sm font-bold text-slate-900">{t('profile.changePassword')}</span>
              <span className="text-xs font-bold text-sky-600">{securityOpen ? t('common.cancel') : t('common.edit')}</span>
            </button>
            {passwordMsg && <Alert variant={passwordMsg.type} onClose={() => setPasswordMsg(null)} style={{ marginTop: '1rem' }}>{passwordMsg.text}</Alert>}
            {securityOpen && <form onSubmit={changePassword} className="mt-4 space-y-4">
              <Field label={t('profile.currentPassword')} type="password" value={password.current} onChange={(value) => { setPassword({ ...password, current: value }); setPasswordErrors((current) => ({ ...current, current: '' })); }} error={passwordErrors.current} />
              <Field label={t('profile.newPassword')} type="password" value={password.next} onChange={(value) => { setPassword({ ...password, next: value }); setPasswordErrors((current) => ({ ...current, next: '' })); }} error={passwordErrors.next} />
              <Field label={t('profile.confirmNewPassword')} type="password" value={password.confirm} onChange={(value) => { setPassword({ ...password, confirm: value }); setPasswordErrors((current) => ({ ...current, confirm: '' })); }} error={passwordErrors.confirm} />
              <button
                className="inline-flex h-9 items-center justify-center rounded-lg border border-slate-200 bg-white px-5 text-sm font-bold text-slate-900 transition hover:border-sky-200 hover:text-sky-600"
                type="submit"
                disabled={changingPassword}
              >
                {changingPassword ? t('profile.changingPassword') : t('profile.changePassword')}
              </button>
            </form>}
          </section>

          <section className="rounded-lg border border-slate-200 bg-white p-4 shadow-soft">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h2 className="text-sm font-bold text-slate-900">{t('profile.aboutTitle', 'Sobre')}</h2>
                <p className="mt-1 text-xs leading-5 text-slate-500">
                  {t('profile.aboutDescription', 'Informacoes do aplicativo e documentos de uso.')}
                </p>
              </div>
              <span className="w-fit rounded-full bg-slate-100 px-3 py-1 text-xs font-bold text-slate-700">
                {t('profile.appVersion', 'Versao do app')}: {APP_VERSION}
              </span>
            </div>
            <div className="mt-4 border-t border-slate-200 pt-4">
              <a
                href="/terms"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex h-9 items-center justify-center rounded-lg border border-slate-200 bg-white px-4 text-sm font-bold text-slate-900 transition hover:border-sky-200 hover:text-sky-600"
              >
                {t('profile.viewTerms', 'Ver termos de uso')}
              </a>
            </div>
          </section>

          <section className="rounded-lg border border-rose-100 bg-white p-3 shadow-soft">
            <h2 className="text-sm font-bold text-rose-700">{t('profile.deleteAccount')}</h2>
            <p className="mt-1 text-xs text-slate-500">{t('profile.deleteAccountHint')}</p>
            <button
              className="mt-3 inline-flex h-8 items-center justify-center rounded-lg border border-rose-200 bg-white px-3 text-xs font-bold text-rose-600 transition hover:bg-rose-50"
              type="button"
              onClick={() => setDeleteStep(1)}
            >
              {t('profile.deleteAccount')}
            </button>
          </section>
        </div>
      </div>

      <Modal isOpen={deleteStep > 0} onClose={() => setDeleteStep(0)} title={deleteStep === 1 ? t('profile.deleteFirstTitle') : t('profile.deleteSecondTitle')}>
        {deleteStep === 1 ? (
          <div className="space-y-4">
            <p className="text-sm text-slate-700">{t('profile.deleteFirstMessage')}</p>
            <textarea
              className={`min-h-24 w-full rounded-lg border bg-slate-50 px-4 py-3 text-sm outline-none focus:border-sky-300 focus:ring-4 focus:ring-sky-100 ${deleteError ? 'border-red-300' : 'border-slate-200'}`}
              value={deleteReason}
              onChange={(event) => {
                setDeleteReason(event.target.value);
                setDeleteError('');
              }}
              placeholder={t('profile.deleteReasonPlaceholder')}
            />
            {deleteError && <p className="text-xs font-semibold text-red-600">{deleteError}</p>}
            <div className="flex justify-end gap-2">
              <button className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-bold" type="button" onClick={() => setDeleteStep(0)}>{t('common.cancel')}</button>
              <button className="rounded-lg bg-rose-600 px-4 py-2 text-sm font-bold text-white" type="button" onClick={() => {
                if (!deleteReason.trim()) {
                  setDeleteError(requiredFieldMessage(t, t('admin.reason')));
                  return;
                }
                setDeleteStep(2);
              }}>{t('common.continue')}</button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <p className="text-sm text-slate-700">{t('profile.deleteSecondMessage')}</p>
            <div className="flex justify-end gap-2">
              <button className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-bold" type="button" onClick={() => setDeleteStep(0)}>{t('common.cancel')}</button>
              <button className="rounded-lg bg-rose-600 px-4 py-2 text-sm font-bold text-white disabled:opacity-60" type="button" onClick={confirmDeleteAccount} disabled={deletingAccount}>
                {deletingAccount ? t('common.deleting') : t('profile.confirmDeleteAccount')}
              </button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

const Field = ({ label, value, onChange, type = 'text', placeholder = '', disabled = false, error }) => (
  <label className="block">
    <span className="mb-2 block text-sm font-semibold text-slate-900">{label}</span>
    <input
      className={`h-10 w-full rounded-lg border bg-slate-50 px-4 text-sm text-slate-900 outline-none transition placeholder:text-slate-500 focus:border-sky-300 focus:ring-4 focus:ring-sky-100 disabled:text-slate-600 ${error ? 'border-red-300' : 'border-slate-200'}`}
      type={type}
      value={value}
      onChange={(event) => onChange?.(event.target.value)}
      placeholder={placeholder}
      disabled={disabled}
    />
    {error && <span className="mt-1 block text-xs font-semibold text-red-600">{error}</span>}
  </label>
);

export default ProfilePage;
